import React, { useCallback, useContext, useEffect, useState } from "react";
import api from "@/lib/api";
import { rupiah, num, formatDate } from "@/lib/format";
import { AuthContext } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Plus, Eye, Trash2, PackageCheck, Ban, CheckCircle2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { rid } from "@/components/UnitSelect";

export const PROD_STATUS = {
  PLANNED: { label: "Direncanakan", cls: "bg-slate-100 text-slate-700" },
  MATERIAL_ISSUED: { label: "Bahan Diambil", cls: "bg-blue-100 text-blue-800" },
  IN_PROGRESS: { label: "Berjalan", cls: "bg-indigo-100 text-indigo-800" },
  WAITING_CHECK: { label: "Menunggu Cek", cls: "bg-amber-100 text-amber-800" },
  COMPLETED: { label: "Selesai", cls: "bg-emerald-100 text-emerald-800" },
  CLOSED: { label: "Closed", cls: "bg-emerald-200 text-emerald-900" },
  CANCELLED: { label: "Dibatalkan", cls: "bg-slate-100 text-slate-400" },
};

export function ProdStatusBadge({ status }) {
  const s = PROD_STATUS[status] || { label: status, cls: "bg-slate-100 text-slate-700" };
  return <Badge className={`${s.cls} font-normal`} data-testid={`prod-status-${status}`}>{s.label}</Badge>;
}

export default function Production() {
  const { user } = useContext(AuthContext);
  const [tab, setTab] = useState("plans");
  const [rows, setRows] = useState([]);
  const [dash, setDash] = useState(null);
  const [recipes, setRecipes] = useState([]);
  const [rates, setRates] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [products, setProducts] = useState([]);
  const [detail, setDetail] = useState(null);
  const [openNew, setOpenNew] = useState(false);
  const [fDate, setFDate] = useState(new Date().toISOString().slice(0, 10));
  const [fProduct, setFProduct] = useState("");
  const [fTarget, setFTarget] = useState(100);
  const [fOps, setFOps] = useState([]);
  const [fNotes, setFNotes] = useState("");
  const [rcpOpen, setRcpOpen] = useState(false);
  const [rProduct, setRProduct] = useState("");
  const [rNotes, setRNotes] = useState("");
  const [rItems, setRItems] = useState([{ _k: rid(), product_id: "", qty: 1, material_type: "countable" }]);
  const [rateOpen, setRateOpen] = useState(false);
  const [rateProduct, setRateProduct] = useState("");
  const [rateValue, setRateValue] = useState(1500);
  const [rateEff, setRateEff] = useState(new Date().toISOString().slice(0, 10));
  const [empOpen, setEmpOpen] = useState(false);
  const [empForm, setEmpForm] = useState({ code: "", name: "", phone: "" });

  const load = useCallback(async () => {
    const [a, b, c, d, e, f] = await Promise.all([
      api.get("/productions"), api.get("/production-dashboard"), api.get("/recipes"),
      api.get("/production-rates"), api.get("/employees"), api.get("/products"),
    ]);
    setRows(a.data); setDash(b.data); setRecipes(c.data); setRates(d.data); setEmployees(e.data); setProducts(f.data);
  }, []);
  useEffect(() => { load().catch((e) => console.error(e)); }, [load]);

  const finishedProducts = products.filter((p) => (p.kind || "finished") === "finished" && p.active);
  const rawProducts = products.filter((p) => p.kind === "raw" && p.active);

  const createPlan = async () => {
    try {
      await api.post("/productions", { date: fDate, product_id: fProduct, target_dus: Number(fTarget), operator_ids: fOps, notes: fNotes });
      toast.success("Rencana produksi dibuat — paket operator dibagi otomatis");
      setOpenNew(false); setFOps([]); setFNotes("");
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal"); }
  };

  const openDetail = async (id) => {
    const r = await api.get(`/productions/${id}`);
    setDetail(r.data);
  };

  const doFinish = async () => {
    if (!window.confirm(`Posting barang jadi ${detail.good_dus} DUS (${num(detail.good_dus * detail.dus_factor)} PCS) ke gudang?`)) return;
    try {
      await api.post(`/productions/${detail.production_id}/finish`);
      toast.success("Barang jadi masuk stok gudang");
      setDetail(null); await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal"); }
  };

  const doClose = async (force = false) => {
    try {
      await api.post(`/productions/${detail.production_id}/close`, { force });
      toast.success("Produksi CLOSED");
      setDetail(null); await load();
    } catch (e) {
      const d = e.response?.data?.detail;
      if (d?.problems) {
        const canForce = ["owner", "manager"].includes(user?.role);
        if (canForce && window.confirm(`Selisih ditemukan:\n• ${d.problems.join("\n• ")}\n\nTetap closing dengan approval Anda?`)) return doClose(true);
        toast.error(d.message || "Closing ditolak");
      } else toast.error(d || "Gagal closing");
    }
  };

  const doCancel = async () => {
    if (!window.confirm(`Batalkan produksi ${detail.prod_no}?`)) return;
    try {
      await api.post(`/productions/${detail.production_id}/cancel`);
      toast.success("Produksi dibatalkan");
      setDetail(null); await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal membatalkan"); }
  };

  const saveRecipe = async () => {
    try {
      await api.post("/recipes", { product_id: rProduct, notes: rNotes, items: rItems.map(({ product_id, qty, material_type }) => ({ product_id, qty: Number(qty), material_type })) });
      toast.success("Resep/BOM tersimpan");
      setRcpOpen(false); setRItems([{ _k: rid(), product_id: "", qty: 1, material_type: "countable" }]);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal"); }
  };

  const saveRate = async () => {
    try {
      await api.post("/production-rates", { product_id: rateProduct, rate_per_dus: Number(rateValue), effective_date: rateEff });
      toast.success("Tarif tersimpan");
      setRateOpen(false); await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal"); }
  };

  const saveEmp = async () => {
    try {
      await api.post("/employees", empForm);
      toast.success("Pegawai ditambahkan");
      setEmpOpen(false); setEmpForm({ code: "", name: "", phone: "" }); await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal"); }
  };

  const delRate = async (id) => {
    if (!window.confirm("Nonaktifkan tarif ini?")) return;
    await api.delete(`/production-rates/${id}`); await load();
  };

  const delRecipe = async (id) => {
    if (!window.confirm("Nonaktifkan resep ini?")) return;
    await api.delete(`/recipes/${id}`); await load();
  };

  return (
    <div data-testid="page-production" className="space-y-6">
      <div>
        <div className="text-xs font-mono uppercase tracking-widest text-slate-500 mb-2">Manufaktur</div>
        <h1 className="font-display text-3xl font-bold text-slate-900">Rencana Produksi</h1>
      </div>

      {dash && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3" data-testid="prod-dashboard">
          <MiniStat testid="dash-today" label="Produksi Hari Ini" value={num(dash.today.count)} sub={`Target ${num(dash.today.target)} dus`} />
          <MiniStat testid="dash-today-good" label="Good Hari Ini" value={num(dash.today.good)} sub={`Reject ${num(dash.today.reject)}`} />
          <MiniStat testid="dash-running" label="Berjalan" value={num(dash.running.count)} warn={dash.running.count > 0} />
          <MiniStat testid="dash-done" label="Selesai" value={num(dash.done.count)} />
          <MiniStat testid="dash-wage" label="Total Upah" value={rupiah(dash.all.wage)} small />
          <MiniStat testid="dash-waste" label="Nilai Waste" value={rupiah(dash.all.waste_value)} small warn={dash.all.waste_value > 0} />
          <MiniStat testid="dash-problems" label="Bermasalah" value={num(dash.problems.length)} warn={dash.problems.length > 0} />
        </div>
      )}
      {dash && dash.problems.length > 0 && (
        <Card className="border-rose-300 bg-rose-50">
          <CardContent className="py-3 text-sm text-rose-800" data-testid="dash-problem-list">
            <b>Produksi bermasalah:</b> {dash.problems.map((p) => `${p.prod_no} (${p.issues} isu)`).join(" · ")}
          </CardContent>
        </Card>
      )}

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="plans" data-testid="tab-plans">Rencana</TabsTrigger>
          <TabsTrigger value="recipes" data-testid="tab-recipes">Resep (BOM)</TabsTrigger>
          <TabsTrigger value="rates" data-testid="tab-rates">Tarif Upah</TabsTrigger>
          <TabsTrigger value="employees" data-testid="tab-employees">Pegawai</TabsTrigger>
        </TabsList>

        <TabsContent value="plans" className="space-y-4 mt-4">
          <div className="flex justify-end">
            <Button className="bg-slate-900 hover:bg-slate-800" onClick={() => setOpenNew(true)} data-testid="btn-new-production"><Plus className="h-4 w-4 mr-1" />Rencana Baru</Button>
          </div>
          <Card>
            <table className="w-full text-sm" data-testid="table-productions">
              <thead><tr className="bg-slate-50 text-left text-xs font-mono uppercase tracking-wider text-slate-500">
                <th className="py-2 px-4">No. Produksi</th><th>Tanggal</th><th>Produk</th><th className="text-right">Target</th>
                <th className="text-right">Good</th><th className="text-right">Reject</th><th className="text-right">Selisih</th>
                <th className="text-right">Upah</th><th>Status</th><th></th>
              </tr></thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.production_id} className="border-b" data-testid={`prod-row-${r.prod_no}`}>
                    <td className="py-2 px-4 font-mono text-xs">{r.prod_no}</td>
                    <td>{formatDate(r.date)}</td>
                    <td>{r.product_name}</td>
                    <td className="text-right font-mono">{num(r.target_dus)} dus</td>
                    <td className="text-right font-mono text-emerald-700">{num(r.good_dus)}</td>
                    <td className="text-right font-mono text-rose-700">{num(r.reject_dus)}</td>
                    <td className={`text-right font-mono ${r.selisih_dus ? "text-rose-700 font-bold" : ""}`}>{num(r.selisih_dus)}</td>
                    <td className="text-right font-mono">{rupiah(r.wage_total)}</td>
                    <td><ProdStatusBadge status={r.status} /></td>
                    <td><Button variant="ghost" size="sm" onClick={() => openDetail(r.production_id)} data-testid={`detail-${r.prod_no}`}><Eye className="h-3.5 w-3.5" /></Button></td>
                  </tr>
                ))}
                {rows.length === 0 && <tr><td colSpan={10} className="py-8 text-center text-slate-400">Belum ada rencana produksi</td></tr>}
              </tbody>
            </table>
          </Card>
        </TabsContent>

        <TabsContent value="recipes" className="space-y-4 mt-4">
          <div className="flex justify-end"><Button className="bg-slate-900 hover:bg-slate-800" onClick={() => setRcpOpen(true)} data-testid="btn-new-recipe"><Plus className="h-4 w-4 mr-1" />Resep Baru</Button></div>
          <div className="grid md:grid-cols-2 gap-4">
            {recipes.filter((r) => r.active).map((r) => (
              <Card key={r.recipe_id} data-testid={`recipe-${r.recipe_id}`}>
                <CardHeader className="pb-2"><CardTitle className="font-display text-base flex items-center justify-between">
                  <span>{r.product_sku} · {r.product_name}</span>
                  <Button variant="ghost" size="sm" onClick={() => delRecipe(r.recipe_id)} data-testid={`del-recipe-${r.recipe_id}`}><Trash2 className="h-3.5 w-3.5 text-rose-600" /></Button>
                </CardTitle></CardHeader>
                <CardContent className="text-sm">
                  <div className="text-xs text-slate-500 mb-1">Kebutuhan per 1 {r.output_unit || "DUS"}:</div>
                  {r.items.map((it, i) => (
                    <div key={i} className="flex justify-between border-b border-slate-100 py-1">
                      <span>{it.name} {it.material_type === "consumable" && <Badge className="bg-amber-100 text-amber-800 font-normal ml-1">Consumable</Badge>}</span>
                      <span className="font-mono">{num(it.qty)} {it.unit}</span>
                    </div>
                  ))}
                </CardContent>
              </Card>
            ))}
            {recipes.filter((r) => r.active).length === 0 && <div className="text-slate-400 text-sm py-8">Belum ada resep</div>}
          </div>
        </TabsContent>

        <TabsContent value="rates" className="space-y-4 mt-4">
          <div className="flex justify-end"><Button className="bg-slate-900 hover:bg-slate-800" onClick={() => setRateOpen(true)} data-testid="btn-new-rate"><Plus className="h-4 w-4 mr-1" />Tarif Baru</Button></div>
          <Card>
            <table className="w-full text-sm" data-testid="table-rates">
              <thead><tr className="bg-slate-50 text-left text-xs font-mono uppercase tracking-wider text-slate-500">
                <th className="py-2 px-4">Produk</th><th className="text-right">Tarif/Dus</th><th>Berlaku Sejak</th><th>Status</th><th></th>
              </tr></thead>
              <tbody>
                {rates.map((r) => (
                  <tr key={r.rate_id} className="border-b">
                    <td className="py-2 px-4">{r.product_sku} · {r.product_name}</td>
                    <td className="text-right font-mono font-bold">{rupiah(r.rate_per_dus)}</td>
                    <td>{formatDate(r.effective_date)}</td>
                    <td>{r.active ? <Badge className="bg-emerald-100 text-emerald-800 font-normal">Aktif</Badge> : <Badge className="bg-slate-100 text-slate-400 font-normal">Nonaktif</Badge>}</td>
                    <td>{r.active && <Button variant="ghost" size="sm" onClick={() => delRate(r.rate_id)} data-testid={`del-rate-${r.rate_id}`}><Trash2 className="h-3.5 w-3.5 text-rose-600" /></Button>}</td>}
                  </tr>
                ))}
                {rates.length === 0 && <tr><td colSpan={5} className="py-8 text-center text-slate-400">Belum ada tarif</td></tr>}
              </tbody>
            </table>
          </Card>
        </TabsContent>

        <TabsContent value="employees" className="space-y-4 mt-4">
          <div className="flex justify-end"><Button className="bg-slate-900 hover:bg-slate-800" onClick={() => setEmpOpen(true)} data-testid="btn-new-employee"><Plus className="h-4 w-4 mr-1" />Pegawai Baru</Button></div>
          <Card>
            <table className="w-full text-sm" data-testid="table-employees">
              <thead><tr className="bg-slate-50 text-left text-xs font-mono uppercase tracking-wider text-slate-500">
                <th className="py-2 px-4">Kode</th><th>Nama</th><th>Telepon</th><th>Status</th>
              </tr></thead>
              <tbody>
                {employees.map((e) => (
                  <tr key={e.employee_id} className="border-b">
                    <td className="py-2 px-4 font-mono">{e.code}</td><td>{e.name}</td><td>{e.phone || "—"}</td>
                    <td>{e.active ? <Badge className="bg-emerald-100 text-emerald-800 font-normal">Aktif</Badge> : <Badge className="bg-slate-100 text-slate-400 font-normal">Nonaktif</Badge>}</td>
                  </tr>
                ))}
                {employees.length === 0 && <tr><td colSpan={4} className="py-8 text-center text-slate-400">Belum ada pegawai</td></tr>}
              </tbody>
            </table>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Dialog: Rencana Baru */}
      <Dialog open={openNew} onOpenChange={setOpenNew}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Rencana Produksi Baru</DialogTitle></DialogHeader>
          <div className="grid gap-3">
            <div><Label>Tanggal</Label><Input type="date" value={fDate} onChange={(e) => setFDate(e.target.value)} data-testid="input-prod-date" /></div>
            <div><Label>Produk (1 rencana = 1 SKU)</Label>
              <Select value={fProduct} onValueChange={setFProduct}>
                <SelectTrigger data-testid="input-prod-product"><SelectValue placeholder="Pilih produk jadi" /></SelectTrigger>
                <SelectContent>{finishedProducts.map((p) => <SelectItem key={p.product_id} value={p.product_id}>{p.sku} · {p.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Target (dus)</Label><Input type="number" value={fTarget} onChange={(e) => setFTarget(e.target.value)} data-testid="input-prod-target" /></div>
            <div>
              <Label>Operator ({fOps.length} dipilih — target dibagi otomatis seimbang)</Label>
              <div className="grid grid-cols-2 gap-1.5 mt-1" data-testid="input-prod-operators">
                {employees.filter((e) => e.active).map((e) => (
                  <label key={e.employee_id} className={`flex items-center gap-2 border rounded px-2.5 py-2 text-sm cursor-pointer ${fOps.includes(e.employee_id) ? "border-slate-900 bg-slate-50" : "border-slate-200"}`} data-testid={`op-${e.code}`}>
                    <input type="checkbox" className="accent-slate-900" checked={fOps.includes(e.employee_id)}
                      onChange={(ev) => setFOps(ev.target.checked ? [...fOps, e.employee_id] : fOps.filter((x) => x !== e.employee_id))} />
                    {e.name}
                  </label>
                ))}
              </div>
            </div>
            <div><Label>Keterangan</Label><Input value={fNotes} onChange={(e) => setFNotes(e.target.value)} data-testid="input-prod-notes" /></div>
          </div>
          <DialogFooter><Button className="bg-slate-900 hover:bg-slate-800" onClick={createPlan} data-testid="btn-save-production">Buat Rencana</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: Detail */}
      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="flex items-center gap-2">{detail?.prod_no} <ProdStatusBadge status={detail?.status} /></DialogTitle></DialogHeader>
          {detail && (
            <div className="space-y-4" data-testid="production-detail">
              <div className="text-sm text-slate-600 flex gap-4 flex-wrap">
                <span>{formatDate(detail.date)}</span><span>{detail.product_sku} · {detail.product_name}</span>
                <span>Target {num(detail.target_dus)} dus</span><span>Tarif {rupiah(detail.wage_rate)}/dus (snapshot {formatDate(detail.wage_rate_date)})</span>
              </div>
              <div className="grid grid-cols-4 gap-3 text-center">
                <div className="bg-slate-50 rounded p-3"><div className="text-xs text-slate-500">Good</div><div className="font-mono font-bold text-emerald-700">{num(detail.good_dus)}</div></div>
                <div className="bg-slate-50 rounded p-3"><div className="text-xs text-slate-500">Reject</div><div className="font-mono font-bold text-rose-700">{num(detail.reject_dus)}</div></div>
                <div className="bg-slate-50 rounded p-3"><div className="text-xs text-slate-500">Selisih</div><div className={`font-mono font-bold ${detail.selisih_dus ? "text-rose-700" : ""}`}>{num(detail.selisih_dus)}</div></div>
                <div className="bg-slate-50 rounded p-3"><div className="text-xs text-slate-500">Total Upah</div><div className="font-mono font-bold">{rupiah(detail.wage_total)}</div></div>
              </div>

              <div>
                <div className="text-xs font-mono uppercase tracking-wider text-slate-500 mb-1">Paket Operator</div>
                <table className="w-full text-sm">
                  <thead><tr className="text-left text-xs text-slate-500 border-b"><th className="py-1">Paket</th><th>Operator</th><th className="text-right">Target</th><th className="text-right">Good</th><th className="text-right">Reject</th><th className="text-right">Upah</th><th>Status</th></tr></thead>
                  <tbody>
                    {detail.packages.map((p) => (
                      <tr key={p.package_id} className="border-b border-slate-100">
                        <td className="py-1.5 font-mono">#{p.seq}</td><td>{p.employee_name}</td>
                        <td className="text-right font-mono">{p.target_dus}</td>
                        <td className="text-right font-mono">{p.good_dus ?? "—"}</td>
                        <td className="text-right font-mono">{p.reject_dus || "—"}{p.reject_reason ? <span className="text-xs text-slate-400 ml-1">({p.reject_reason})</span> : null}</td>
                        <td className="text-right font-mono">{p.good_dus != null ? rupiah(p.good_dus * detail.wage_rate) : "—"}</td>
                        <td>{p.status === "SELESAI" ? <Badge className="bg-emerald-100 text-emerald-800 font-normal">Selesai</Badge> : p.status === "SELISIH" ? <Badge className="bg-rose-100 text-rose-800 font-normal">Selisih</Badge> : <Badge className="bg-slate-100 text-slate-500 font-normal">Pending</Badge>}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div>
                <div className="text-xs font-mono uppercase tracking-wider text-slate-500 mb-1">Bahan (teoritis vs diambil/terpakai/kembali/waste)</div>
                <table className="w-full text-sm">
                  <thead><tr className="text-left text-xs text-slate-500 border-b">
                    <th className="py-1">Bahan</th><th>Tipe</th><th className="text-right">Teoritis</th><th className="text-right">Diambil</th>
                    <th className="text-right">Terpakai</th><th className="text-right">Kembali</th><th className="text-right">Waste</th><th className="text-right">Alokasi/Paket</th>
                  </tr></thead>
                  <tbody>
                    {detail.materials.map((m) => (
                      <tr key={m.product_id} className="border-b border-slate-100">
                        <td className="py-1.5">{m.sku} · {m.name}</td>
                        <td>{m.material_type === "consumable" ? <Badge className="bg-amber-100 text-amber-800 font-normal">Consumable</Badge> : <Badge className="bg-slate-100 text-slate-600 font-normal">Countable</Badge>}</td>
                        <td className="text-right font-mono">{num(m.theoretical_qty)} {m.unit}</td>
                        <td className="text-right font-mono">{num(m.issued_qty)}</td>
                        <td className="text-right font-mono">{num(m.used_qty)}</td>
                        <td className="text-right font-mono">{num(m.returned_qty)}</td>
                        <td className="text-right font-mono">{num(m.waste_qty)}</td>
                        <td className="text-right font-mono text-xs text-slate-500">{(m.per_package || []).join(" / ")}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="flex gap-2 justify-end border-t pt-3">
                {detail.status === "WAITING_CHECK" && <Button className="bg-emerald-700 hover:bg-emerald-800" onClick={doFinish} data-testid="btn-finish-production"><PackageCheck className="h-4 w-4 mr-1" />Posting Barang Jadi ({num(detail.good_dus)} dus)</Button>}
                {detail.status === "COMPLETED" && <Button className="bg-slate-900 hover:bg-slate-800" onClick={() => doClose(false)} data-testid="btn-close-production"><CheckCircle2 className="h-4 w-4 mr-1" />Closing Produksi</Button>}
                {["PLANNED", "MATERIAL_ISSUED", "IN_PROGRESS"].includes(detail.status) && <Button variant="outline" onClick={doCancel} data-testid="btn-cancel-production"><Ban className="h-4 w-4 mr-1" />Batalkan</Button>}
                {detail.status === "PLANNED" && <p className="text-xs text-slate-500 mr-auto flex items-center gap-1"><AlertTriangle className="h-3.5 w-3.5" /> Lanjutkan ke menu Pengambilan Bahan</p>}
                {["MATERIAL_ISSUED", "IN_PROGRESS"].includes(detail.status) && <p className="text-xs text-slate-500 mr-auto">Input hasil di menu Paket & Hasil, lalu rekonsiliasi bahan</p>}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Dialog: Resep Baru */}
      <Dialog open={rcpOpen} onOpenChange={setRcpOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>Resep / BOM Baru</DialogTitle></DialogHeader>
          <div className="grid gap-3">
            <div><Label>Produk Jadi</Label>
              <Select value={rProduct} onValueChange={setRProduct}>
                <SelectTrigger data-testid="input-recipe-product"><SelectValue placeholder="Pilih produk" /></SelectTrigger>
                <SelectContent>{finishedProducts.map((p) => <SelectItem key={p.product_id} value={p.product_id}>{p.sku} · {p.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Kebutuhan bahan per 1 DUS</Label>
              {rItems.map((it, i) => (
                <div key={it._k} className="flex gap-2 mt-1.5 items-center">
                  <Select value={it.product_id} onValueChange={(v) => { const n = [...rItems]; n[i].product_id = v; setRItems(n); }}>
                    <SelectTrigger data-testid={`recipe-item-${i}`}><SelectValue placeholder="Bahan baku" /></SelectTrigger>
                    <SelectContent>{rawProducts.map((p) => <SelectItem key={p.product_id} value={p.product_id}>{p.sku} · {p.name}</SelectItem>)}</SelectContent>
                  </Select>
                  <Input type="number" className="w-24" value={it.qty} onChange={(e) => { const n = [...rItems]; n[i].qty = e.target.value; setRItems(n); }} data-testid={`recipe-qty-${i}`} />
                  <Select value={it.material_type} onValueChange={(v) => { const n = [...rItems]; n[i].material_type = v; setRItems(n); }}>
                    <SelectTrigger className="w-36" data-testid={`recipe-type-${i}`}><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="countable">Countable</SelectItem><SelectItem value="consumable">Consumable</SelectItem></SelectContent>
                  </Select>
                  <Button variant="ghost" size="sm" onClick={() => setRItems(rItems.filter((_, j) => j !== i))}><Trash2 className="h-3.5 w-3.5 text-rose-600" /></Button>
                </div>
              ))}
              <Button variant="outline" size="sm" className="mt-2" onClick={() => setRItems([...rItems, { _k: rid(), product_id: "", qty: 1, material_type: "countable" }])} data-testid="btn-add-recipe-item"><Plus className="h-3.5 w-3.5 mr-1" />Tambah Bahan</Button>
            </div>
            <div><Label>Catatan</Label><Input value={rNotes} onChange={(e) => setRNotes(e.target.value)} /></div>
          </div>
          <DialogFooter><Button className="bg-slate-900 hover:bg-slate-800" onClick={saveRecipe} data-testid="btn-save-recipe">Simpan Resep</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: Tarif Baru */}
      <Dialog open={rateOpen} onOpenChange={setRateOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Tarif Produksi Baru</DialogTitle></DialogHeader>
          <div className="grid gap-3">
            <div><Label>Produk</Label>
              <Select value={rateProduct} onValueChange={setRateProduct}>
                <SelectTrigger data-testid="input-rate-product"><SelectValue placeholder="Pilih produk" /></SelectTrigger>
                <SelectContent>{finishedProducts.map((p) => <SelectItem key={p.product_id} value={p.product_id}>{p.sku} · {p.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Tarif per Dus (Rp)</Label><Input type="number" value={rateValue} onChange={(e) => setRateValue(e.target.value)} data-testid="input-rate-value" /></div>
            <div><Label>Berlaku Sejak</Label><Input type="date" value={rateEff} onChange={(e) => setRateEff(e.target.value)} data-testid="input-rate-eff" /></div>
            <p className="text-xs text-slate-500">Tarif tersimpan (snapshot) di setiap transaksi produksi — perubahan tarif tidak mengubah upah produksi lama.</p>
          </div>
          <DialogFooter><Button className="bg-slate-900 hover:bg-slate-800" onClick={saveRate} data-testid="btn-save-rate">Simpan Tarif</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: Pegawai Baru */}
      <Dialog open={empOpen} onOpenChange={setEmpOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Pegawai Baru</DialogTitle></DialogHeader>
          <div className="grid gap-3">
            <div><Label>Kode</Label><Input value={empForm.code} onChange={(e) => setEmpForm({ ...empForm, code: e.target.value })} data-testid="input-emp-code" /></div>
            <div><Label>Nama</Label><Input value={empForm.name} onChange={(e) => setEmpForm({ ...empForm, name: e.target.value })} data-testid="input-emp-name" /></div>
            <div><Label>Telepon</Label><Input value={empForm.phone} onChange={(e) => setEmpForm({ ...empForm, phone: e.target.value })} /></div>
          </div>
          <DialogFooter><Button className="bg-slate-900 hover:bg-slate-800" onClick={saveEmp} data-testid="btn-save-employee">Simpan</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function MiniStat({ testid, label, value, sub, warn, small }) {
  return (
    <div data-testid={testid} className="bg-white rounded-lg border border-slate-200 p-4">
      <div className="text-[10px] font-mono uppercase tracking-wider text-slate-500">{label}</div>
      <div className={`font-mono font-bold ${small ? "text-sm" : "text-xl"} mt-1 ${warn ? "text-amber-700" : "text-slate-900"}`}>{value}</div>
      {sub && <div className="text-[10px] text-slate-400">{sub}</div>}
    </div>
  );
}
